import { Box, Stack, Typography } from '@mui/material'
import { DeleteOutline, HideImageOutlined, LoopOutlined } from '@mui/icons-material'
import { FileAPIEntry, FileAction, FileState, UseFileAPIReturn } from '../FileAPI'
import { ReactNode, useMemo, useRef } from 'react'

import { CreativeUploadWizardStep } from './_main/CreativeUploadWizard.controller'
import { FileAPIUpload } from '../FileAPIUpload'
import { GalleryImage } from '../Gallery/GalleryImage'
import { MUIBadge } from '../MUIBadge'
import { MUIButton } from '../MUIButton'
import { MUITooltip } from '../MUITooltip'
import { supportedImageFileTypes } from 'constants/misc'
import { useTranslation } from 'react-i18next'

/** Interface describing config flags for ImageItem actions */
interface Config {
  /** Toggles availability of delete button */
  allowDelete: boolean
  /** Toggles availability of replace file button */
  allowReplace: boolean
  /** Toggles availability of retry button */
  allowRetry: boolean
}

/** Props for Image Item component */
interface Props<T extends { [key: string]: any }> {
  /** File to be displayed */
  file: FileAPIEntry<T>
  /** File controller instance */
  fileCtrl: UseFileAPIReturn
  /** Optional config as object with flags - can be partial  */
  config?: Partial<Config>
  /** Slot for extra button action to be displayed in top right corner */
  extraActionsSlot?: ReactNode
}

/**
 * Item used for representing visual file listed by FileAPI.  
 *   
 * Config flags allow to turn on/off delete, retry and replace functionality.  
 * Own actions can be added using *extraActionsSlot*  
 *   
 * All states and cases are handled using provided fileController instance.  
 *   
 * @example
 * <ImageItem<{ favorited: boolean }>
 *  file={fileEntry}
 *  fileCtrl={fileControllerInstance}
 * />
 */
export function ImageItem<T extends { [key: string]: any } = {}>({
  file,
  fileCtrl,
  extraActionsSlot,
  config = {},
}: Props<T>) {
  const { t } = useTranslation(['image_upload_item'])

  const inputRef = useRef<HTMLInputElement>(null)

  const normalizedConfig: Config = useMemo(() => ({
    allowDelete: false,
    allowRetry: false,
    allowReplace: false,
    ...config,
  }), [config])

  return (
    <>

      <Box display="none">
        <FileAPIUpload
          ref={inputRef}
          acceptedFileTypes={supportedImageFileTypes}
          uploadHandler={(acceptedFiles) => fileCtrl.reUploadFile(acceptedFiles[0], file.id, {
            tag: CreativeUploadWizardStep.REQUIRED_VISUALS,
            onSuccess: () => {
              fileCtrl.loadFiles([file.id])
            },
          })}
          files={[]}
          showFileListing={false}
          maxNumberOfFiles={1}
        />
      </Box>

      <GalleryImage
        label={file.originalFilename ?? file.gcFilename ?? undefined}
        imageUrl={file.displayUrl ?? undefined}
        isLoading={file.state === FileState.RUNNING}
        isError={file.action !== FileAction.LOAD && file.state === FileState.ERROR}
        errorMessage={file.error ?? undefined}
      >

        {file.action === FileAction.UPLOAD && file.state === FileState.RUNNING &&
          <MUIBadge
            sx={{
              position: 'absolute',
              top: '1rem',
              left: '1rem'
            }}
            label={`${file.progress}%`}
          />
        }

        {file.action === FileAction.LOAD && file.state === FileState.RUNNING &&
          <MUIBadge
            sx={{
              position: 'absolute',
              top: '1rem',
              left: '1rem'
            }}
            label={t('processing')}
          />
        }

        <Stack
          sx={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
          }}
          direction="row"
          gap={1}
        >

          {normalizedConfig.allowDelete && file.state !== FileState.RUNNING &&
            <MUIButton
              type="orangeSecondaryBorder"
              size="sm"
              isIconButton
              onClick={() => fileCtrl.deleteFiles([file.id])}
            >
              <DeleteOutline color="error" fontSize="large" />
            </MUIButton>
          }

          {file.action === FileAction.LOAD && file.state !== FileState.RUNNING &&
            <>

              {normalizedConfig.allowReplace &&
                <MUITooltip content={t('replace')}>
                  <MUIButton
                    size="sm"
                    isIconButton
                    onClick={() => inputRef.current?.click()}
                  >
                    <LoopOutlined fontSize="large" />
                  </MUIButton>
                </MUITooltip>
              }
            </>
          }

          {normalizedConfig.allowRetry && (file.action === FileAction.UPLOAD || file.action === FileAction.RE_UPLOAD) && file.state === FileState.ERROR &&
            <MUITooltip content={t('retry')}>
              <MUIButton
                size="sm"
                isIconButton
                onClick={() => fileCtrl.reUploadFile(file.fileObject, file.id, {
                  tag: CreativeUploadWizardStep.REQUIRED_VISUALS,
                  onSuccess: () => fileCtrl.loadFiles([file.id]),
                })}
              >
                <LoopOutlined fontSize="large" />
              </MUIButton>
            </MUITooltip>
          }

          {extraActionsSlot}

        </Stack>

        {file.action === FileAction.LOAD && file.state === FileState.ERROR &&
          <Stack gap={.5} alignItems="center">
            <HideImageOutlined fontSize="large" />
            <Typography variant="text-sm">
              {(t('no_thumbnail'))}
            </Typography>
          </Stack>
        }

      </GalleryImage>
    </>
  )
}
